import { apiGet, apiPost, apiDelete } from './client';

// ===== Types =====

export interface JoinRequest {
  id: number;
  userId: number;
  projectId: number;
  projectName: string | null;
  requestedStartDate: string;
  state: 'pending' | 'approved' | 'declined';
  declineReason: string | null;
  approvedBy: { id: number; name: string | null } | null;
  assignmentId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PendingJoinRequest {
  id: number;
  userId: number;
  userName: string | null;
  userEmail: string;
  projectId: number;
  projectName: string | null;
  requestedStartDate: string;
  state: string;
  userHourlyCosts: number | null;
  createdAt: string | null;
}

export interface CreateJoinRequestInput {
  projectId: number;
  requestedStartDate: string;
}

export interface ApproveJoinRequestInput {
  // Phase 109 Plan 06 (D109-04): null means "no rate/cost set" and is
  // distinct from 0 ("deliberately non-billable") — the invoice-generation
  // guard reads these two states differently.
  billingRate: number | null;
  hourlyCosts: number | null;
  userInvestment: number | null;
  roleName: string;
}

export interface DeclineJoinRequestInput {
  reason?: string;
}

// ===== API Functions =====

export async function createJoinRequest(input: CreateJoinRequestInput): Promise<JoinRequest> {
  const res = await apiPost<{ data: JoinRequest }>('/join-requests', input);
  return res.data;
}

export async function getMyJoinRequests(state?: string): Promise<JoinRequest[]> {
  const params = new URLSearchParams();
  if (state) params.set('state', state);
  const query = params.toString();
  const res = await apiGet<{ data: JoinRequest[] }>(`/join-requests/my${query ? `?${query}` : ''}`);
  return res.data;
}

export async function getPendingJoinRequests(scope?: 'all'): Promise<PendingJoinRequest[]> {
  const url =
    scope === 'all' ? '/admin/join-requests/pending?scope=all' : '/admin/join-requests/pending';
  const res = await apiGet<{ data: PendingJoinRequest[] }>(url);
  return res.data;
}

export async function approveJoinRequest(id: number, input: ApproveJoinRequestInput): Promise<JoinRequest> {
  const res = await apiPost<{ data: JoinRequest }>(`/admin/join-requests/${id}/approve`, input);
  return res.data;
}

export async function declineJoinRequest(id: number, input: DeclineJoinRequestInput): Promise<JoinRequest> {
  const res = await apiPost<{ data: JoinRequest }>(`/admin/join-requests/${id}/decline`, input);
  return res.data;
}

export async function deleteJoinRequest(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/join-requests/${id}`);
}
